import * as React from 'react';
import { Link, RouteComponentProps } from '@reach/router';
import { gql, useQuery } from '@apollo/client';
import Container from 'react-bootstrap/Container';
import InputGroup from 'react-bootstrap/InputGroup';
import FormControl from 'react-bootstrap/FormControl';
import Select from 'react-select';
import * as GetPaginatedPhotoshootsByNameTypes from './__generated__/GetPaginatedPhotoshootsByName';
import * as GetUsersTypes from './__generated__/GetUsers';
import * as GetCurrentUserTypes from './__generated__/GetCurrentUser';
import SecondaryHeader from './SecondaryHeader';
import Thumbnail from './Thumbnail';
import PhotoGrid from './PhotoGrid';
import './PhotoshootListPage.scss';
import LoadingIndicator from './LoadingIndicator';
import ShareButton from 'components/elements/ShareButton';
import PaginationRow, {
  DEFAULT_PAGE_SIZE,
} from 'components/elements/PaginationRow';
import { sharePhotoshootModalOpenVar } from 'uiState';
import { delayedValueUpdate } from './elements/ManagedFormControl';
import SearchIcon from 'bootstrap-icons/icons/search.svg';
import XIcon from 'bootstrap-icons/icons/x-lg.svg';
import { CURRENT_USER_FRAGMENT } from '../types/users';

const GET_PAGINATED_PHOTOSHOOTS_BY_NAME = gql`
  query GetPaginatedPhotoshootsByName($page: Int, $pageSize: Int, $searchInput: String, $ownerUserIds: [Int]) {
    paginatedPhotoshootsByName(page: $page, pageSize: $pageSize, searchInput: $searchInput, ownerUserIds: $ownerUserIds) {
      photoshoots {
        __typename
        id
        slug
        name
        thumbnailUrl
        isOwner
        user {
          id
          displayName
          email
        }
      }
      pagination {
        currentPage
        totalPages
        pageSize
        recordCount
      }
    }
  }
`;

const GET_USERS = gql`
  query GetUsers {
    users {
      id
      displayName
      email
    }
  }
`;

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      ...CurrentUser
    }
  }
  ${CURRENT_USER_FRAGMENT}
`;

interface PhotoshootListPageProps extends RouteComponentProps {}

interface UserOption {
  value: number;
  label: string;
}

const PhotoshootListPage: React.FC<PhotoshootListPageProps> = () => {
  const [page, setPage] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = React.useState<string | undefined>(undefined);
  const [inputValue, setInputValue] = React.useState<string>('');
  const [selectedOwners, setSelectedOwners] = React.useState<UserOption[]>([]);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Fetch current user to check admin status
  const { data: currentUserData } = useQuery<GetCurrentUserTypes.GetCurrentUser>(GET_CURRENT_USER);
  const isAdmin = currentUserData?.me?.isAdmin || false;

  // Fetch users for the owner filter (only if admin)
  const { data: usersData } = useQuery<GetUsersTypes.GetUsers>(GET_USERS, {
    skip: !isAdmin,
  });

  const { data, loading, error, previousData } =
    useQuery<
      GetPaginatedPhotoshootsByNameTypes.GetPaginatedPhotoshootsByName,
      GetPaginatedPhotoshootsByNameTypes.GetPaginatedPhotoshootsByNameVariables
    >(GET_PAGINATED_PHOTOSHOOTS_BY_NAME, {
      variables: {
        page,
        pageSize,
        searchInput,
        ownerUserIds: selectedOwners.length > 0 ? selectedOwners.map(o => o.value) : undefined,
      },
    });

  // Use previous data while loading to keep the component tree stable
  const displayData = data || previousData;

  // Prepare user options for the owner filter
  const userOptions: UserOption[] = React.useMemo(() => {
    if (!usersData?.users) return [];
    return usersData.users.map((user) => ({
      value: user.id,
      label: user.displayName || user.email,
    }));
  }, [usersData]);

  if (error) {
    return <Container fluid>Virhe: {error}</Container>;
  }
  if (loading && !searchInput) {
    return (
      <Container fluid>
        <LoadingIndicator />
      </Container>
    );
  }
  if (!displayData) {
    return (
      <Container fluid>
        <LoadingIndicator />
      </Container>
    );
  }
  const {
    photoshoots,
    pagination: { totalPages, recordCount },
  } = displayData.paginatedPhotoshootsByName;
  return (
    <div className="PhotoshootListPage">
      <SecondaryHeader path="Omat kuvat" />
      <Container fluid>
        {isAdmin && (
        <div className="search-section">
          <div className="search-bars-container">
            <div className="search-input-wrapper">
              <InputGroup className="search-input-group">
                <InputGroup.Prepend>
                  <InputGroup.Text>
                    <img src={SearchIcon} alt="Haku" className="search-icon" />
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <FormControl
                  ref={inputRef}
                  type="text"
                  placeholder="Hae kuvakansiota..."
                  value={inputValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInputValue(value);
                    delayedValueUpdate(value, (newInput: string) => {
                      if (!value) {
                        setSearchInput(undefined);
                      } else {
                        setSearchInput(newInput);
                        setPage(1);
                      }
                    });
                  }}
                />
              </InputGroup>
              {inputValue && (
                <button
                  className="clear-button"
                  onClick={() => {
                    setInputValue('');
                    setSearchInput(undefined);
                    setPage(1);
                    inputRef.current?.focus();
                  }}
                  title="Tyhjennä haku"
                  type="button"
                >
                  <img src={XIcon} alt="Tyhjennä" className="clear-icon" />
                </button>
              )}
            </div>
            {isAdmin && (
              <div className="owner-filter-wrapper">
                <Select<UserOption, true>
                  isMulti
                  options={userOptions}
                  value={selectedOwners}
                  onChange={(selected: readonly UserOption[] | null) => {
                    setSelectedOwners((selected || []) as UserOption[]);
                    setPage(1);
                  }}
                  placeholder="Suodata omistajan mukaan..."
                  isClearable
                  className="owner-filter-select"
                  classNamePrefix="owner-filter"
                />
              </div>
            )}
          </div>
          <div>
            <span className="results-count">
              {recordCount === 1
                ? '1 kuvakansio löytyi'
                : `${recordCount} kuvakansiota löytyi`}
            </span>
          </div>
        </div>
        )}
        <PhotoGrid>
          {photoshoots.map((photoshoot) => (
            <React.Fragment key={photoshoot.slug}>
              {(photoshoot.isOwner || isAdmin) && (
                <ShareButton
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    sharePhotoshootModalOpenVar(photoshoot.id);
                  }}
                />
              )}
              <Link to={`/kuvat/${photoshoot.slug}`}>
                <Thumbnail
                  src={photoshoot.thumbnailUrl}
                  alt={photoshoot.name}
                  caption={photoshoot.name}
                  subcaption={/*"1.1.2021" TODO: get actual date!*/ ''}
                  ownerName={isAdmin ? (photoshoot.user.displayName || photoshoot.user.email) : undefined}
                />
              </Link>
            </React.Fragment>
          ))}
        </PhotoGrid>
        <PaginationRow
          currentPage={page}
          totalPages={totalPages}
          pageSize={pageSize}
          recordCount={recordCount}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </Container>
    </div>
  );
};

export default PhotoshootListPage;
